import { type QueryKey } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useTestRunEditDialog } from '@/features/executions/components/useTestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildTestDetailPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { buildTestingEntityMetricColgroup } from '@/features/shared/components/tableSystem/TableV2MetricColumnLayout';
import {
  TableV2MetricCells,
  TableV2MetricHeaders,
  type TableV2MetricSortField,
} from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2ProjectCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { type TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { buildTagColorByName } from '@/features/shared/tags';
import { DeleteTestDialog, EditTestDialog } from '@/features/tests/components/dialogs/TestDialogs';
import { resolveTestRunChildCount } from '@/features/tests/components/TestsTableV2.helpers';
import { TestExpandedRuns } from '@/features/tests/components/TestsTableV2NestedRuns';
import { useTestsTableEditDialog } from '@/features/tests/components/useTestsTableEditDialog';
import { useTestsTableRowActions } from '@/features/tests/components/useTestsTableRowActions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  MetricThresholdGroup,
  ProjectDto,
  QueryResult,
  TestListItemDto,
  TestRunDto,
} from '@flowqa/shared';

type TestsTableV2Props = {
  active: boolean;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  initialPageSize?: number;
  noRowsText?: string;
  pageSizeOptions?: number[];
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  queryKey: QueryKey;
  search?: string;
  selectedProjectId?: string | null;
  showMetricColumns?: boolean;
  sort?: ApiSort;
  tagOptionsOverride?: TagOption[];
  testSuiteSecondaryItemsResolver?: (
    row: TestListItemDto,
  ) => Array<{ href?: string | null; label: string }>;
  testSuiteNameResolver?: (row: TestListItemDto) => string | null;
  nestedTestRunsLatestOnly?: boolean;
  testRunsScopeKey?: string;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  onQueryMeta?: (meta: { total: number; items: TestListItemDto[] } | null) => void;
  onSortChange?: (sort: ApiSort) => void;
};

type SortableTestField = 'displayName' | TableV2MetricSortField | 'latestRunTimestamp';

const defaultTestsV2Sort = { field: 'latestRunTimestamp', direction: 'desc' } satisfies ApiSort;

const baseSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];
const buildSkeletonColumns = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
): TableV2SkeletonColumnKind[] => [
  ...baseSkeletonColumns.slice(0, 4),
  ...visibleMetricKeys.map(() => 'metric' as const),
  ...baseSkeletonColumns.slice(4, 5),
  ...(includeProject ? (['project'] satisfies TableV2SkeletonColumnKind[]) : []),
  ...baseSkeletonColumns.slice(5),
];

const buildColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
): ReactElement => buildTestingEntityMetricColgroup(visibleMetricKeys, includeProject);

const TestRow = ({
  deleteRowPendingId,
  isLastVisibleRow,
  row,
  rowProject,
  suiteName,
  suiteSecondaryItems,
  showProjectColumn,
  tagOptions,
  thresholds,
  testRunsScopeKey,
  testRunsQueryFn,
  nestedTestRunsLatestOnly,
  visibleMetricKeys,
  updateRowTagsColorPendingId,
  runDeleteRowPendingId,
  runUpdateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteRunRow,
  onEditRow,
  onEditRunRow,
  onUpdateRowTagsColor,
  onUpdateRunRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  row: TestListItemDto;
  rowProject: Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'> | null;
  suiteName: string | null;
  suiteSecondaryItems: Array<{ href?: string | null; label: string }>;
  showProjectColumn: boolean;
  tagOptions: TagOption[];
  thresholds: MetricThresholdGroup;
  testRunsScopeKey?: string;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  nestedTestRunsLatestOnly: boolean;
  visibleMetricKeys: MetricThresholdGroupKey[];
  updateRowTagsColorPendingId: string | null;
  runDeleteRowPendingId: string | null;
  runUpdateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: TestListItemDto) => void;
  onDeleteRunRow: (row: TestRunDto) => void;
  onEditRow: (row: TestListItemDto) => void;
  onEditRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: {
    row: TestListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateRunRowTagsColor: (input: { row: TestRunDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const tagColorByName = buildTagColorByName(tagOptions);
  const latestRun = row.latestRun ?? null;
  const metrics = row.metrics ?? null;
  const testName = row.displayName ?? row.name;
  const testRunCount = resolveTestRunChildCount(row);

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={buildTestDetailPath(row.slug)}
            primary={testName}
            secondary={suiteSecondaryItems.length === 0 ? suiteName : null}
            secondaryItems={suiteSecondaryItems}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={latestRun?.status ?? null} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={latestRun?.timestamp ?? null} />
        </TableCell>
        <TableV2MetricCells
          metrics={metrics}
          thresholds={thresholds}
          visibleMetricKeys={visibleMetricKeys}
        />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={rowProject} fallbackName={row.project?.name} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={testName}
            verticalAlign="center"
            deleteActionLabel={getEntityActionLabel('delete', 'Test')}
            editActionLabel={getEntityActionLabel('edit', 'Test')}
            editTagsEntityNoun="test"
            rowTags={row.tags ?? []}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableDelete={deleteRowPendingId === row.id}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={buildSkeletonColumns(visibleMetricKeys, showProjectColumn).length}>
          <TestExpandedRuns
            childCount={testRunCount}
            deleteRowPendingId={runDeleteRowPendingId}
            expanded={expanded}
            organisationId={row.organisationId}
            projectId={row.projectId}
            testId={row.id}
            latestOnly={nestedTestRunsLatestOnly}
            latestRunId={latestRun?.id ?? null}
            tagColorByName={tagColorByName}
            testRunsScopeKey={testRunsScopeKey}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={runUpdateRowTagsColorPendingId}
            onDeleteRunRow={onDeleteRunRow}
            onEditRunRow={onEditRunRow}
            onUpdateRunRowTagsColor={onUpdateRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const TestsTableV2 = ({
  active,
  fixedFilters,
  forceLoading = false,
  initialPageSize,
  noRowsText = 'No data',
  pageSizeOptions,
  onQueryMeta,
  projectOptions = [],
  queryFn,
  queryKey,
  search,
  selectedProjectId = null,
  showMetricColumns = true,
  sort: controlledSort,
  tagOptionsOverride = [],
  testSuiteNameResolver,
  testSuiteSecondaryItemsResolver,
  nestedTestRunsLatestOnly = false,
  testRunsScopeKey,
  testRunsQueryFn,
  onSortChange,
}: TestsTableV2Props): ReactElement => {
  const [localSort, setLocalSort] = useState<ApiSort>(defaultTestsV2Sort);
  const sort = controlledSort ?? localSort;
  const showProjectColumn = selectedProjectId === null;
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const visibleMetricKeys = showMetricColumns
    ? resolveVisibleMetricThresholdKeys(thresholds.tests)
    : [];
  const skeletonColumns = buildSkeletonColumns(visibleMetricKeys, showProjectColumn);
  const projectById = useMemo(
    () => new Map(projectOptions.map((project) => [project.id, project])),
    [projectOptions],
  );
  const {
    editEditor,
    editErrors,
    editForm,
    editTarget,
    handleEditClose,
    handleEditRow,
    handleEditSave,
  } = useTestsTableEditDialog();
  const {
    editLoading: runEditLoading,
    editTarget: runEditTarget,
    onEditClose: onRunEditClose,
    onEditRow: onEditRunRow,
    onEditSave: onRunEditSave,
  } = useTestRunEditDialog(selectedProjectId);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useTestsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: runDeleteLoading,
    deleteRowPendingId: runDeleteRowPendingId,
    deleteTarget: runDeleteTarget,
    onDeleteClose: onRunDeleteClose,
    onDeleteConfirm: onRunDeleteConfirm,
    onDeleteRow: onDeleteRunRow,
    onUpdateRowTagsColor: onUpdateRunRowTagsColor,
    updateRowTagsColorPendingId: runUpdateRowTagsColorPendingId,
  } = useTestRunsTableRowActions({ selectedProjectId });

  const handleSortChange = (field: SortableTestField): void => {
    const nextSort: ApiSort = {
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    };
    setLocalSort(nextSort);
    onSortChange?.(nextSort);
  };

  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="displayName"
          label="Test"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Latest Status" />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="latestRunTimestamp"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableV2MetricHeaders
        sort={sort}
        visibleMetricKeys={visibleMetricKeys}
        onSortChange={handleSortChange}
      />
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Tests"
        colgroup={buildColgroup(visibleMetricKeys, showProjectColumn)}
        emptyText={noRowsText}
        entityLabel="tests"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        initialPageSize={initialPageSize}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={[sort]}
        onQueryMeta={onQueryMeta}
        renderRow={(row, context) => (
          <TestRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            row={row}
            rowProject={projectById.get(row.projectId) ?? null}
            suiteName={
              testSuiteNameResolver?.(row) ??
              row.suites?.primary?.displayName ??
              row.suites?.primary?.name ??
              null
            }
            suiteSecondaryItems={testSuiteSecondaryItemsResolver?.(row) ?? []}
            runDeleteRowPendingId={runDeleteRowPendingId}
            runUpdateRowTagsColorPendingId={runUpdateRowTagsColorPendingId}
            showProjectColumn={showProjectColumn}
            tagOptions={tagOptionsOverride}
            thresholds={thresholds.tests}
            nestedTestRunsLatestOnly={nestedTestRunsLatestOnly}
            testRunsScopeKey={testRunsScopeKey}
            testRunsQueryFn={testRunsQueryFn}
            visibleMetricKeys={visibleMetricKeys}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onDeleteRunRow={onDeleteRunRow}
            onEditRow={handleEditRow}
            onEditRunRow={onEditRunRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
            onUpdateRunRowTagsColor={onUpdateRunRowTagsColor}
          />
        )}
      />
      <EditTestDialog
        open={editTarget !== null}
        saving={editEditor.isPending}
        values={editForm.values}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        errors={{ name: editErrors.fieldErrorProps('name') }}
        errorMessage={editForm.submitError}
        onClose={handleEditClose}
        onSave={handleEditSave}
        handlers={{
          onNameChange: (value) => {
            editForm.setField('name', value);
          },
        }}
      />
      <TestRunEditDialog
        open={runEditTarget !== null}
        execution={runEditTarget}
        loading={runEditLoading}
        onClose={onRunEditClose}
        onSave={onRunEditSave}
      />
      <DeleteTestDialog
        open={deleteTarget !== null}
        testName={deleteTarget?.name}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={runDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={runDeleteLoading}
        onClose={onRunDeleteClose}
        onConfirm={onRunDeleteConfirm}
      />
    </>
  );
};
